import React from 'react';

import { DEMAND_COLORS } from './format';

/**
 * Headline figures without a box each: a band of a few groups split by thin rules, each led by one number and
 * carrying a small picture of what stands behind it (the months, the customers), so the figures read as a summary
 * rather than a wall of equal tiles.
 */
export const StatBand: React.FC<{
  children: React.ReactNode;
  /** In a card of its own (true), or bare inside a section that already is one */
  boxed?: boolean;
}> = ({ children, boxed = true }) => (
  <div
    className={`grid grid-cols-1 md:grid-cols-3 gap-y-5 md:gap-y-0 divide-y md:divide-y-0 md:divide-x divide-gray-200 ${
      boxed ? 'rounded-lg border bg-white shadow-sm px-4 py-5 sm:px-6' : ''
    }`}
  >
    {children}
  </div>
);

interface StatProps {
  label: string;
  value: string;
  /** Smaller words after the value, e.g. "of 11 months" */
  suffix?: string;
  /** Hover text explaining the figure */
  title?: string;
  /** The picture under the value */
  children?: React.ReactNode;
  /** Lines under the picture */
  detail?: React.ReactNode;
}

export const Stat: React.FC<StatProps> = ({
  label,
  value,
  suffix,
  title,
  children,
  detail,
}) => (
  <div
    className="pt-5 first:pt-0 md:pt-0 md:px-6 md:first:pl-0 md:last:pr-0"
    title={title}
  >
    <p className="text-sm text-gray-600">{label}</p>
    <p className="mt-1 flex items-baseline gap-2">
      <span className="text-3xl font-semibold text-gray-900">{value}</span>
      {suffix && <span className="text-sm text-gray-600">{suffix}</span>}
    </p>
    {children && <div className="mt-3">{children}</div>}
    {detail && <div className="mt-2 text-sm text-gray-600">{detail}</div>}
  </div>
);

/** One column per month, from 0; the last (current, to date) lighter. Hover a column for its month and value. */
export const MiniColumns: React.FC<{
  values: { label: string; value: number; partial?: boolean }[];
  format: (value: number) => string;
}> = ({ values, format }) => {
  const max = Math.max(1, ...values.map((v) => v.value));
  return (
    <div className="flex items-end gap-0.5 h-10" aria-hidden="true">
      {values.map((v) => (
        <span
          key={v.label}
          title={`${v.label}: ${format(v.value)}`}
          className="flex-1 rounded-t-sm"
          style={{
            height: `${Math.max(v.value > 0 ? 8 : 2, (Math.max(0, v.value) / max) * 100)}%`,
            background: v.value > 0 ? DEMAND_COLORS.customer : '#e5e7eb',
            opacity: v.partial ? 0.4 : 1,
          }}
        />
      ))}
    </div>
  );
};

/** One square per complete month: filled when it had demand */
export const MonthStrip: React.FC<{
  months: { label: string; active: boolean }[];
}> = ({ months }) => (
  <div className="flex flex-wrap gap-1" aria-hidden="true">
    {months.map((m) => (
      <span
        key={m.label}
        title={m.label}
        className="w-3.5 h-3.5 rounded-sm"
        style={{ background: m.active ? DEMAND_COLORS.customer : '#e5e7eb' }}
      />
    ))}
  </div>
);

const SHARE_COLORS = [DEMAND_COLORS.customer, '#8fb8ea', '#d1d5db'];

/** Shares of a whole, left to right, split by a 2px gap; the first in the accent, then lighter */
export const ShareBar: React.FC<{
  shares: { label: string; share: number }[];
}> = ({ shares }) => (
  <div className="flex h-2.5 gap-0.5" aria-hidden="true">
    {shares
      .filter((s) => s.share > 0)
      .map((s, i) => (
        <span
          key={s.label}
          title={s.label}
          className="h-full first:rounded-l-full last:rounded-r-full"
          style={{
            width: `${s.share * 100}%`,
            background: SHARE_COLORS[Math.min(i, SHARE_COLORS.length - 1)],
          }}
        />
      ))}
  </div>
);

/**
 * A value against a minimum: the bar fills to the value, a tick marks the minimum, and the fill turns amber when
 * the value is below it (the caller says so in words too)
 */
export const Meter: React.FC<{ value: number; minimum: number }> = ({
  value,
  minimum,
}) => {
  const scale = Math.max(1, value, minimum) * 1.25;
  const below = minimum > 0 && value < minimum;
  return (
    <div className="relative h-2.5 rounded-full bg-blue-100" aria-hidden="true">
      <span
        className="absolute inset-y-0 left-0 rounded-full"
        style={{
          width: `${(Math.max(0, value) / scale) * 100}%`,
          background: below ? '#d97706' : DEMAND_COLORS.customer,
        }}
      />
      {minimum > 0 && (
        <span
          className="absolute -top-1 -bottom-1 w-0.5 bg-gray-700"
          style={{ left: `${(minimum / scale) * 100}%` }}
        />
      )}
    </div>
  );
};
